import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link,
  Paper,
  CssBaseline,
  Button,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Header from '../../../shared/header/Header';

const headings = [
  { id: 'install-prerequisites', label: 'Install Prerequisites' },
  { id: 'create-new-project', label: 'Create New Project' },
  { id: 'set-up-env', label: 'Set Up Environment Variables (Azure Keys)' },
  { id: 'run-locally', label: 'Run the Code Locally' },
  { id: 'testing', label: 'Testing the Setup' },
  { id: 'code', label: 'Agent Code' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'notes', label: 'Notes' },
];

const ByoAgentGuide: React.FC = () => {
  const theme = useTheme();
  const isMobile = theme.breakpoints.down('md');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [positionFixed, setPositionFixed] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(headings[0].id);

  useEffect(() => {
    const currentRef = scrollRef.current;
    const handleScrollPos = () => {
      if (currentRef) {
        const scrollTop = currentRef.scrollTop;
        setPositionFixed(scrollTop > 30);
      }
    };
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScrollPos);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScrollPos);
      }
    };
  }, []);

  useEffect(() => {
    const currentRef = scrollRef.current;
    if (!currentRef) return;

    const handleScroll = () => {
      if (currentRef.scrollTop < 10) {
        setActiveSection(headings[0].id);
        return;
      }
      let current = headings[0].id;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && currentRef) {
          const rect = el.getBoundingClientRect();
          const containerRect = currentRef.getBoundingClientRect();
          if (rect.top < containerRect.bottom - 80 && rect.bottom > containerRect.top + 80) {
            current = h.id;
          }
        }
      }
      if (Math.abs(currentRef.scrollHeight - currentRef.scrollTop - currentRef.clientHeight) < 2) {
        current = headings[headings.length - 1].id;
      }
      setActiveSection(current);
    };

    currentRef.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => currentRef.removeEventListener('scroll', handleScroll);
  }, []);

  // Sidebar navigation scroll handler
  const handleSidebarNav = (id: string) => {
    const el = document.getElementById(id);
    const container = scrollRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const scrollTop = container.scrollTop + (elRect.top - containerRect.top) - 24;
      container.scrollTo({ top: scrollTop, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  return (
    <>
      <CssBaseline />
      <Header />
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="center"
        bgcolor="#f8f9fa"
        height={isMobile ? 'auto' : '100vh'}
        px={isMobile ? 1 : 3}
        pt={isMobile ? 8 : 12}
        component="div"
        ref={scrollRef}
        sx={{
          overflowY: 'auto',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Sidebar */}
          <Paper
            elevation={2}
            sx={{
              bgcolor: '#f3f6fa',
              borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
              borderBottom: isMobile ? '1px solid #e0e0e0' : 'none',
              p: isMobile ? 2 : 3,
              mb: isMobile ? 2 : 0,
              position: isMobile ? 'static' : positionFixed ? 'fixed' : 'absolute',
              top: isMobile ? '0' : positionFixed ? '65px' : '0',
              left: isMobile ? 0 : positionFixed ? 0 : '-23px',
              bottom: 0,
            }}
          >
            <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
              Contents
            </Typography>
            <Divider sx={{ marginY: 2, borderColor: '#1976d2' }} />
            <List disablePadding>
              {headings.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <ListItem
                    key={item.id}
                    disableGutters
                    sx={{
                      mb: 1,
                      py: 0,
                      bgcolor: isActive ? '#1976d2' : 'inherit',
                      color: isActive ? '#fff' : 'inherit',
                      borderRadius: 1,
                      transition: 'background 0.2s',
                    }}
                  >
                    <Link
                      href={`#${item.id}`}
                      underline="hover"
                      color={isActive ? 'inherit' : 'primary'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSidebarNav(item.id);
                      }}
                      sx={{
                        width: '100%',
                        display: 'block',
                        px: 1.5,
                        color: isActive ? '#fff' : 'inherit',
                        borderRadius: 1,
                        transition: 'background 0.2s, color 0.2s',
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          fontWeight: isActive ? 'bold' : 'normal',
                        }}
                      />
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
          {/* Main content */}
          <Box
            component="main"
            flex={1}
            ref={scrollRef} // <-- Move ref here!
            sx={{
              marginLeft: { xs: 0, sm: '220px', md: 1 },
              width: `calc(100% - 300px)`,
              overflowY: 'auto', // <-- Move scroll styles here!
              minHeight: '100vh',
              maxHeight: '100vh', // Optional
            }}
          >
            <Box
              sx={{
                p: isMobile ? 2 : 5,
                bgcolor: '#fff',
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h4'} color="primary" fontWeight="bold" gutterBottom>
                BYO Azure Agent Guide
              </Typography>
              <Divider sx={{ marginY: 2 }} />

              <Typography paragraph>
                This guide provides step-by-step instructions to set up and run the agent locally. The guide aims to
                help software developers to create agents successfully.
              </Typography>

              <Section id="install-prerequisites" title="1. Install Prerequisites">
                <ul>
                  <li>
                    <Typography>Install Python 3.10+ and pip.</Typography>
                  </li>
                  <li>
                    <Typography>Use your preferred IDE (e.g., VS Code, PyCharm).</Typography>
                  </li>
                </ul>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', fontWeight: 500, mt: 2 }}>
                  Install Node.js and Verify Node.js Installation
                </Typography>
                <ul>
                  <li>
                    <Typography>
                      Download and install Node.js (
                      <Link href="https://nodejs.org/" target="_blank">
                        https://nodejs.org/
                      </Link>
                      )
                    </Typography>
                  </li>
                  <li>
                    <Typography>Verify Node.js installation:</Typography>
                    <Command>
                      node -v
                      <br />
                      npm -v
                    </Command>
                  </li>
                </ul>
              </Section>

              <Section id="create-new-project" title="2. Create New Project">
                <ul>
                  <li>
                    <Typography>
                      Download {' '}
                      <Link
                        href="https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/summarise_meeting_requirements_agent.zip"
                        target="_blank"
                      >
                        Azure sample code
                      </Link>{' '}
                      and unzip the zip file.
                    </Typography>
                  </li>
                  <li>
                    <Typography>Go to File → New Project, provide the agent folder's path in your IDE.</Typography>
                  </li>
                </ul>
              </Section>

              <Section id="set-up-env" title="3. Set Up Environment Variables (Azure Keys)">
                <Typography>
                  Create a <b>.env</b> file in the <b>services/</b> directory with the following variables:
                </Typography>
                 <Typography>
                  Use these environment variables to configure your Azure OpenAI client and test
                  the connection locally.
                </Typography>
                <Command>
                  AZURE_DEPLOYMENT_NAME=your-azure-deployment-name
                  <br />
                  AZURE_OPENAI_MODEL_NAME=your-model-name
                  <br />
                  AZURE_OPENAI_API_VERSION=your-api-version
                  <br />
                  AZURE_OPENAI_ENDPOINT_BASE_URL=https://your-endpoint.openai.azure.com/
                  <br />
                  AZURE_OPENAI_API_KEY=your-azure-openai-api-key
                </Command>
              </Section>

              <Section id="run-locally" title="4. Run the Code Locally">
                <Typography fontWeight="bold" sx={{ mt: 1 }}>
                  Option 1: Run Bash Setup Script
                </Typography>
                <Typography>From the project root directory:</Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>For Mac OS/Linux:</Typography>
                <Command>bash setup.sh</Command>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>For Windows:</Typography>
                <Command>.\setup.ps1</Command>
                <Typography>This will install dependencies and set up the environment automatically.</Typography>

                <Typography fontWeight="bold" sx={{ mt: 2 }}>
                  Option 2: Manual Commands
                </Typography>
                <Typography fontWeight="bold" sx={{ color: '#1976d2', mt: 2 }}>
                  Step 1: Create Python Environment and Activate It
                </Typography>
                <Typography>
                  Open a terminal in the <b>services/</b> directory and run:
                </Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>
                  Create a virtual environment
                </Typography>
                <Command>python -m venv venv</Command>

                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>
                  Activate a virtual environment:
                </Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>For Mac OS/Linux:</Typography>
                <Command>source venv/bin/activate</Command>
                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>For Windows:</Typography>
                <Command>venv\Scripts\activate</Command>

                <Typography sx={{ fontStyle: 'italic', color: '#1976d2', mt: 1 }}>
                  Install required dependencies for your agent using pip.
                </Typography>
                <Command>pip install -r &lt;path to requirements.txt&gt;</Command>

                <Typography fontWeight="bold" sx={{ color: '#1976d2', mt: 2 }}>
                  Step 2: Run Backend API
                </Typography>
                <Command>
                  cd services
                  <br />
                  python &lt;path to main.py&gt; or
                  <br />
                  uvicorn main:app --host 0.0.0.0 --port 8001
                </Command>
                <Typography>
                  API will be available at{' '}
                  <Link href="http://localhost:8001" target="_blank">
                    http://localhost:8001
                  </Link>
                </Typography>

                <Typography fontWeight="bold" sx={{ color: '#1976d2', mt: 2 }}>
                  Step 3: Run UI (React App)
                </Typography>
                <Typography>Open a new terminal window and run:</Typography>
                <Command>
                  cd ui
                  <br />
                  npm install
                  <br />
                  npm start
                </Command>
                <Typography>
                  UI will be available at{' '}
                  <Link href="http://localhost:3000" target="_blank">
                    http://localhost:3000
                  </Link>
                </Typography>
              </Section>

              <Section id="testing" title="5. Testing the Setup">
                <ul>
                  <li>
                    <Typography>
                      Open your browser and go to the UI URL (e.g.,{' '}
                      <Link href="http://localhost:3000" target="_blank">
                        http://localhost:3000
                      </Link>
                      ).
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The UI should communicate with the backend API at{' '}
                      <Link href="http://localhost:8001" target="_blank">
                        http://localhost:8001
                      </Link>
                      .
                    </Typography>
                  </li>
                </ul>
              </Section>

              <Section id="code" title="6. Agent Code">
                <Typography fontWeight="bold" sx={{ color: '#1976d2', mt: 1 }}>
                  Agent Logic
                </Typography>
                <ul>
                  <li>
                    <Typography>Loads Azure OpenAI client using environment variables.</Typography>
                  </li>
                  <li>
                    <Typography>It automatically summarizes meeting multiple transcripts or notes.</Typography>
                  </li>
                  <li>
                    <Typography> Handles chat interaction asynchronously.</Typography>
                  </li>
                </ul>
                <Typography fontWeight="bold" sx={{ color: '#1976d2', mt: 1 }}>
                  React App
                </Typography>
                <ul>
                  <li>Initializes the agent and chat history.</li>
                  <li>Accepts document uploads.</li>
                  <li>Extracts text from .docx, .pdf and .txt.</li>
                  <li>Sends content to the agent and displays the response.</li>
                </ul>
              </Section>

              <Section id="troubleshooting" title="7. Troubleshooting">
                <ul>
                  <li>Ensure your .env variables are correct and loaded.</li>
                  <li>Check backend logs for errors (e.g., missing keys, 500 errors).</li>
                </ul>
              </Section>

              <Section id="notes" title="Notes:">
                <ul>
                  <li>Adjust paths if your UI or backend is in a different folder.</li>
                  <li>For any issues, check the logs and ensure all dependencies are installed.</li>
                </ul>
              </Section>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const Section: React.FC<{
  id: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <Box id={id} mb={5}>
    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', mt: 3 }} gutterBottom>
      {title}
    </Typography>
    <Box component="div" sx={{ pl: 3 }}>
      {children}
    </Box>
  </Box>
);

const Command: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(children as string);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#333',
        color: '#fff',
        p: 2,
        borderRadius: 1,
        mb: 2,
        overflowX: 'auto',
      }}
    >
      <Button
        onClick={copyToClipboard}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </Button>
      <pre>{children}</pre>
    </Box>
  );
};

export default ByoAgentGuide;
